import React, { useEffect, useState, ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { AxiosResponse } from 'axios';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import LazySpinner from '../common/LazySpinner';

interface ConsentGuardProps {
  children: ReactNode;
}

export default function ConsentGuard({ children }: ConsentGuardProps): React.JSX.Element | null {
  const { storeNo } = useParams<{ storeNo: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchData } = useAxiosFetch();
  const [allowed, setAllowed] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(true);

  // ConsentGuard 전용 동의 체크 — utils/consent.ts의 checkStoreConsentAsync(raw axios, refresh 없음)
  // 대신 useAxiosFetch(401→refresh→재시도)를 쓴다. 토큰이 만료된 순간 진입하면 raw axios는
  // 그대로 401이라 "미동의"로 오판해 /consent로 튕기고, 다시 돌아오면 또 튕기는 루프가 생겼다.
  // KaKaoCallback/GoogleCallback/NaverCallback 은 로그인 직후(토큰 방금 발급)라 이 문제가 없어
  // checkStoreConsentAsync 를 그대로 둔다 — 범위를 이 가드에만 좁힌다.
  const checkConsent = async (): Promise<boolean> => {
    if (!storeNo) return false;
    try {
      const profileRes = await fetchData({ method: 'get', url: '/profile' });
      const userNo = (profileRes as AxiosResponse)?.data?.data?.userNo;
      if (!userNo) return false;

      const termsRes = await fetchData({
        method: 'get',
        url: `/terms/${Number(storeNo)}/${userNo}`,
      });
      const termsData = (termsRes as AxiosResponse)?.data?.data;
      return (
        termsData?.termsServiceYn === 'Y' && termsData?.termsIndividualYn === 'Y'
      );
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async (): Promise<void> => {
      if (!storeNo) {
        if (mounted) {
          setAllowed(false);
          setChecking(false);
        }
        return;
      }
      if (location.pathname === `/${storeNo}` || location.pathname === `/${storeNo}/`) {
        if (mounted) {
          setAllowed(true);
          setChecking(false);
        }
        return;
      }

      setChecking(true);
      try {
        const ok = await checkConsent();
        if (!mounted) {
          return;
        }
        if (ok) {
          setAllowed(true);
          setChecking(false);
          return;
        }
        if (!location.pathname.startsWith(`/${storeNo}/consent`)) {
          const currentPath = location.pathname + location.search;
          const existingTarget = sessionStorage.getItem('globalLoginTarget');
          if (!existingTarget || !existingTarget.includes('/consent')) {
            sessionStorage.setItem('globalLoginTarget', currentPath);
          }
          navigate(`/${storeNo}/consent`, { replace: true, state: { from: location } });
        }
        if (mounted) {
          setAllowed(false);
          setChecking(false);
        }
      } catch {
        if (!mounted) {
          return;
        }
        if (!location.pathname.startsWith(`/${storeNo}/consent`)) {
          navigate(`/${storeNo}/consent`, { replace: true, state: { from: location } });
        }
        setAllowed(false);
        setChecking(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
    // fetchData(useAxiosFetch)는 매 렌더마다 새로 생성돼 deps에 넣으면 무한 루프가 된다
    // (StoreConsent.js의 동일 패턴과 같은 이유로 제외).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNo, location.pathname, location.search, navigate, location]);

  if (checking) {
    return <LazySpinner />;
  }
  if (!allowed) {
    return null;
  }
  return <>{children}</>;
}
